import React, { useEffect, useState, useCallback, useRef, } from 'react';
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useNavigate } from 'react-router-dom';
import { useModeratorsQuery } from '../../hooks/useModeratorsQuery';
import useCleanPaginationURL from '../../hooks/useCleanPaginationURL';
import { usePaginationTextReplace } from '../../hooks/usePaginationTextReplace';
import { MetricType, getMetricTitle } from '../../types/api/metrics/metricsType';
import useDebounce from '../../hooks/useDebounce';
import { useAlert } from '../../context/alert';
import { MetricPermissionModel, ModeratorResponse } from '../../types/api/moderators';
import { deleteModerator } from '../../services/moderators';
import { pageSize } from '../../services/settings';
import { Placeholder, createErrorPlaceholder } from '../basic';
import TableActionsBanner from '../common/TableActionsBanner';
import { ModeratorModal } from './components/ModeratorModal';
import { getPermissionGroups } from '../moderators/ModerationList+Data';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { truncateText } from '../../helpers/strings';
import { Button, TextInput, Stack, Text, Pagination } from '@primer/react'
import { Table, DataTable } from '@primer/react/experimental'
import { moderatorsTableConfig } from './ModerationList+Table';
import { XCircleIcon, SearchIcon, XCircleFillIcon, PlusIcon, XIcon } from '@primer/octicons-react';

export default function ModerationList() {

    const navigate = useNavigate();

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)
    const [pageIndex, setPageIndex] = React.useState(1)
    const [openSuggestionModal, setOpenSuggestionModal] = React.useState(false)
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);
    const { setSuccess, setInfo, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();

    const {
        isLoading,
        data,
        refetch,
        isFetched,
        isError
    } = useModeratorsQuery(
        debouncedSearchQuery,
        pageIndex,
        pageSize,
        undefined
    );

    const { mutate: deleteModeratorRequest, isPending } = useMutation({
        mutationFn: ({ id }: { id: string }) => deleteModerator([id]),
        onSuccess: (data) => {
            dismissTableActionAlert()
            setInfo("Модератор удален")
            refetch()
        }
    });

    useCleanPaginationURL();
    usePaginationTextReplace()

    useEffect(() => {
        refetch()
    }, [pageIndex]);

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    const didModeratorCreated = () => {
        setOpenSuggestionModal(false)
        refetch()
    }


    const suggestionDismissed = () => {
        setOpenSuggestionModal(false)
    }

    const createModerator = () => {
        setOpenSuggestionModal(true)
    }

    const getMetricsPermisisonsTitle = (selectedMetricPermissions: MetricPermissionModel) => {
        const totalCount = Object.values(MetricType).length;
        const selectedMetrics = Object.values(selectedMetricPermissions).filter(value => value === true).length;
        const isAllMetricSelected = selectedMetrics === Object.values(selectedMetricPermissions).length
        const selectedCount = Object.values(selectedMetricPermissions).filter(Boolean).length;

        const isSelectedAll = selectedCount === totalCount && isAllMetricSelected
        const selectedMetricsTitle = isSelectedAll ? ' (Все)' : ` (${getSelectedMetricPermissionsLabels(selectedMetricPermissions)})`

        return 'Просмотр доступных метрик' + selectedMetricsTitle;
    }

    const getSelectedMetricPermissionsLabels = (selectedMetricPermissions: MetricPermissionModel): string => {
        const labels: string[] = [];

        Object.entries(selectedMetricPermissions).forEach(([key, isSelected]) => {
            if (isSelected) {
                const title = getMetricTitle(key as MetricType);
                labels.push(title);
            }
        });

        return labels.join(', ');
    };

    const showDeleteModeratorAlert = useCallback((moderator: ModeratorResponse) => {
        dismissTableActionAlert()

        let props = createConfirmDeleteAlertProps(moderator)
        const alertId = addCustomAlert(
            TableActionsBanner,
            props,
            {
                autoClose: false,
            }
        );

        setActionsAlertId(alertId)
        actionsAlertIdRef.current = alertId
    }, [actionsAlertId]);

    function createConfirmDeleteAlertProps(moderator: ModeratorResponse) {
        return {
            title: 'Точно хотите удалить модератора?',
            count: undefined,
            buttons: [
                <Button key="no-button" variant="default" onClick={dismissTableActionAlert}>
                    Нет
                </Button>,
                <Button key="yes-button" variant="danger" disabled={isPending} onClick={() => deleteModeratorRequest({ id: moderator.id })}>
                    Да, удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const emptyListDescription = `В сервисе не создано
    ни одного модератора`;
    const emptySearchDescription = `По вашему запросу не найдено
    подходящих модераторов`;

    const cellStyles = {
        display: 'block',
        margin: 0,
        alignSelf: 'flex-start'
    };

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <div></div>
                    <Stack direction="horizontal">
                        <TextInput
                            leadingVisual={SearchIcon}
                            trailingAction={
                                searchQuery.length > 0 ? (
                                    <TextInput.Action
                                        onClick={() => {
                                            setSearchQuery('');
                                        }}
                                        icon={XCircleFillIcon}
                                        aria-label="Очистить"
                                    />
                                ) : undefined
                            }
                            value={searchQuery}
                            placeholder='Поиск'
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                            }}
                        />
                        <Button
                            variant="default"
                            size="small"
                            onClick={createModerator}
                            leadingVisual={PlusIcon}
                        >
                            Создать модератора
                        </Button>
                    </Stack>
                </Stack>
            }
            content={
                <Stack>
                    {openSuggestionModal && <ModeratorModal
                        isOpen={openSuggestionModal}
                        onDidSaved={didModeratorCreated}
                        onClose={suggestionDismissed}
                    />}

                    {isError && !isLoading && createErrorPlaceholder()}
                    {isLoading && <Table.Container>
                        <Table.Skeleton
                            aria-labelledby="moderators-loading"
                            rows={pageSize}
                            columns={moderatorsTableConfig().map((el) => {
                                return {
                                    header: el.header,
                                    id: el.field,
                                }
                            })}
                        />
                    </Table.Container>}
                    {isFetched && data?.list.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header={searchQuery.length > 0 ? 'Ничего не было найдено' : 'Модераторы не найдены'}
                            description={searchQuery.length > 0 ? emptySearchDescription : emptyListDescription}
                            button={searchQuery.length > 0 && <Button
                                variant="default"
                                leadingVisual={XIcon}
                                onClick={clearSearchInput}>
                                Очистить</Button>}
                        />
                    )}
                    {isFetched && data && data.list.length > 0 &&
                        <Table.Container>
                            <DataTable
                                aria-labelledby="users-table"
                                data={data.list}
                                columns={[
                                    {
                                        id: 'name',
                                        header: "Наименование",
                                        renderCell: (row) => {
                                            const moderator = (row as ModeratorResponse);
                                            return (
                                                <div style={cellStyles}>
                                                    {moderator.name}
                                                </div>
                                            )
                                        }
                                    },
                                    {
                                        id: 'login',
                                        header: "Логин",
                                        maxWidth: 274,
                                        renderCell: (row) => {
                                            const moderator = (row as ModeratorResponse);
                                            return (
                                                <div style={cellStyles}>
                                                    {moderator.username}
                                                </div>
                                            )
                                        }
                                    },
                                    {
                                        id: 'password',
                                        header: "Пароль",
                                        maxWidth: 274,
                                        renderCell: (row) => {
                                            const moderator = (row as ModeratorResponse);
                                            return (
                                                <div style={cellStyles}>
                                                    {moderator.password}
                                                </div>
                                            )
                                        }
                                    },
                                    {
                                        id: 'access',
                                        header: "Доступы",
                                        renderCell: (row) => {
                                            const moderator = (row as ModeratorResponse);
                                            const filteredGroups = getPermissionGroups(moderator.permissions);
                                            const moderatorData = moderator.permissions

                                            if (moderatorData) {
                                                const permissionGroups = getPermissionGroups(moderatorData);
                                                const list = (
                                                    <Stack gap='condensed'>
                                                        {permissionGroups.map((group, groupIndex) => (
                                                            <Stack key={groupIndex}
                                                                direction="vertical"
                                                                gap='none' >
                                                                <Text
                                                                    fontSize={12}
                                                                    fontWeight="regular"
                                                                    color="fg.muted"
                                                                    lineHeight="medium">
                                                                    {group.title}
                                                                </Text>
                                                                {group.items.map((item, itemIndex) => (
                                                                    <Text
                                                                        key={itemIndex}
                                                                        fontSize={14}
                                                                        fontWeight="medium"
                                                                        color="fg.default"
                                                                        lineHeight="medium">
                                                                        {item.label}
                                                                    </Text>
                                                                ))}
                                                            </Stack>
                                                            // TODO text for metrics (getMetricsPermisisonsTitle)
                                                        ))}
                                                    </Stack>
                                                );
                                                return list
                                            }

                                            return ("")
                                        }
                                    },
                                    {
                                        id: 'actons',
                                        header: '',
                                        maxWidth: '100px',
                                        renderCell: (row) => {
                                            const moderator = (row as ModeratorResponse);
                                            return (
                                                <Button
                                                    onClick={() => showDeleteModeratorAlert(moderator)}
                                                    variant='danger'
                                                    size="small"
                                                    disabled={actionsAlertId !== undefined}
                                                    sx={{
                                                        display: 'block',
                                                        margin: 0,
                                                        // marginTop: '20px',
                                                        alignSelf: 'flex-start'
                                                    }}>
                                                    Удалить
                                                </Button>
                                            )
                                        }
                                    }
                                ]}
                            />
                        </Table.Container>
                    }
                    {isFetched && data && data.list.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination aria-label="pagination" pageSize={pageSize} totalCount={data.totalCount} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        ></RegularPageLayout>

    )
}
